<script setup lang="ts">
type PerkIcon =
  | "pond"
  | "lotus"
  | "fountain"
  | "rose"
  | "promenade"
  | "fitness";

interface Perk {
  icon: PerkIcon;
  label: string;
  note?: string;
}

interface Stat {
  value: string;
  label: string;
}

interface GroundsBlock {
  header: string;
  intro?: string;
  note?: string;
  image: string;
  image_alt?: string;
  stats?: Stat[];
  perks: Perk[];
}

defineProps<{
  block: GroundsBlock;
}>();

const { t } = useI18n();
const localePath = useLocalePath();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-emerald-50/40 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute top-1/3 -right-16 h-72 w-72 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-lime-200/20 blur-3xl dark:bg-emerald-600/5"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header class="mb-5 md:mb-7">
        <span
          class="font-body text-xs uppercase tracking-[0.3em] text-emerald-700 dark:text-emerald-400"
        >
          {{ t("amenities.grounds.eyebrow") }}
        </span>
        <h2
          class="mt-3 max-w-5xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-3 max-w-3xl font-body text-base leading-relaxed text-slate-600 md:mt-4 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div class="grid gap-4 md:grid-cols-12 md:gap-5">
        <article
          class="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl md:col-span-7 dark:border-slate-700 dark:bg-slate-800/60"
        >
          <div class="aspect-[4/3] h-full w-full overflow-hidden md:absolute md:inset-0 md:aspect-auto">
            <NuxtImg
              :src="block.image"
              :alt="block.image_alt ?? block.header"
              loading="lazy"
              width="1400"
              height="1000"
              sizes="100vw md:60vw"
              class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
          </div>
        </article>

        <div class="flex flex-col gap-3 md:col-span-5 md:gap-3.5">
        <div
          class="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-1 md:gap-3.5 lg:grid-cols-2"
        >
          <div
            v-for="perk in block.perks"
            :key="perk.icon + perk.label"
            class="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3.5 shadow-sm backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-emerald-400 hover:shadow-md dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-emerald-500/60"
          >
            <span
              class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
            >
              <svg
                v-if="perk.icon === 'pond'"
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <ellipse cx="12" cy="14" rx="8" ry="5" />
                <path d="M7 14c0-1 1-2 2-2s1 1 2 1 1-1 2-1 2 1 2 2-1 2-2 2-1-1-2-1-1 1-2 1-2-1-2-2z" />
                <circle cx="9" cy="14" r="0.5" fill="currentColor" />
                <path d="M12 5l1.5 2.5L12 10l-1.5-2.5z" />
              </svg>
              <svg
                v-else-if="perk.icon === 'lotus'"
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 4c-1 3-1 7 0 9 1-2 1-6 0-9z" />
                <path d="M5 9c1 2 4 5 7 6-1-3-4-5-7-6z" />
                <path d="M19 9c-1 2-4 5-7 6 1-3 4-5 7-6z" />
                <path d="M4 14c2 2 5 3 8 3s6-1 8-3c-3 3-5 5-8 5s-5-2-8-5z" />
              </svg>
              <svg
                v-else-if="perk.icon === 'fountain'"
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3v4" />
                <path d="M9 5c1 1 2 1 3 2 1-1 2-1 3-2" />
                <path d="M6 13c0-3 2-6 6-6s6 3 6 6" />
                <path d="M3 17h18" />
                <path d="M5 21h14" />
                <path d="M12 13v4" />
              </svg>
              <svg
                v-else-if="perk.icon === 'rose'"
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="9" r="4.5" />
                <path d="M10 9c.5-1.5 2-2.5 3.5-2" />
                <path d="M12 9c.5-1 1.5-1.5 2.5-1" />
                <path d="M12 13.5v7.5" />
                <path d="M12 17c-2-.5-3-2-2.5-4" />
                <path d="M12 19c2-.5 3-2 2.5-4" />
              </svg>
              <svg
                v-else-if="perk.icon === 'promenade'"
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21c2-6 4-10 6-18" />
                <path d="M15 21c-2-6-4-10-6-18" />
                <path d="M4 16c4 1 8-1 10-3" />
                <path d="M4 10c4 1 8-1 10-3" />
                <path d="M20 14c-2-1-4-.5-6 .5" />
              </svg>
              <svg
                v-else-if="perk.icon === 'fitness'"
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <rect x="2" y="9" width="2.5" height="6" rx="0.6" />
                <rect x="19.5" y="9" width="2.5" height="6" rx="0.6" />
                <rect x="5" y="10.5" width="2" height="3" rx="0.4" />
                <rect x="17" y="10.5" width="2" height="3" rx="0.4" />
                <path d="M7 12h10" />
              </svg>
            </span>
            <div class="flex flex-col">
              <span
                class="font-display text-base font-semibold italic text-slate-900 dark:text-white"
              >
                {{ perk.label }}
              </span>
              <span
                v-if="perk.note"
                class="mt-0.5 font-body text-xs text-slate-500 dark:text-slate-400"
              >
                {{ perk.note }}
              </span>
            </div>
          </div>
        </div>

          <ul
            v-if="block.stats?.length"
            class="order-first flex flex-col gap-3"
          >
            <li
              v-for="stat in block.stats"
              :key="stat.value + stat.label"
              class="relative overflow-hidden rounded-2xl border border-slate-200 bg-white/70 px-5 py-4 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/50"
            >
              <div
                aria-hidden="true"
                class="absolute left-0 top-4 h-8 w-0.5 rounded-r-full bg-emerald-400 dark:bg-emerald-500"
              />
              <span
                class="block font-display text-2xl font-semibold tabular-nums text-emerald-800 md:text-3xl dark:text-emerald-200"
              >
                {{ stat.value }}
              </span>
              <span
                class="mt-1 block font-body text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400"
              >
                {{ stat.label }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div
        class="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5 md:mt-6"
      >
        <p
          v-if="block.note"
          class="inline-flex items-start gap-2 font-body text-sm text-slate-600 dark:text-slate-300"
        >
          <svg
            class="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>{{ block.note }}</span>
        </p>
        <NuxtLink
          :to="localePath('/location')"
          class="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-emerald-400/60 bg-emerald-50 px-4 py-2 font-body text-sm font-semibold text-emerald-800 shadow-sm transition-all duration-300 hover:border-emerald-500 hover:bg-emerald-100 hover:shadow-md dark:border-emerald-400/40 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
        >
          {{ t('amenities.links.howToGetThere') }}
          <svg
            class="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
